import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, Dialog, DialogContent, DialogTitle, Divider, InputBase, Stack, Typography} from '@mui/material';
import SearchIcon from '../../assets/icons/SearchIcon';
import {ChartType, chartTypeFromJSON} from '../../proto/generated/chart';
import {useQuickSearchSettings} from './QuickSearchSettings';
import {iconMap, QuickSearchFunctions} from './QuickSearchFunctions';
import {useDispatch, useSelector} from 'react-redux'
import {selectActiveChartId, selectChartConfig, selectChartConfigSettings, selectChartTypes} from '../../selectors/chart';
import {RootState} from '../../store';
import {ViewType} from '../../assets/constants';
import {getIcon} from '../chartTypeMenu/ChartTypeMenu';
import {combinedDrawingToolItems, getDrawingIcon} from './QuickSearchButton';
import {updateChartType} from '../../reducer/chart.reducer';
import {handleCreateAlert, listOfFunctions, onToggleFullscreen, settingsFunction} from './QuickSearchFunction';
import {AppContext} from '../../App';
import {addRecentItem} from '../../reducer/recentSearch.reducer';
import {DrawingDisplayType, DrawingTypes} from '../chartV2/drawingTools/drawing.interface';
import {ExtraSmallButton} from "../custom";
import ClearIcon from "../../assets/icons/ClearIcon";

interface QuickSearchDialogProps {
    open: boolean;
    onClose: () => void;
}

const QuickSearchDialog: React.FC<QuickSearchDialogProps> = ({open, onClose}) => {
    const appContext = useContext(AppContext)
    const viewKey = useSelector(selectActiveChartId)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const [checkboxStates, setCheckboxStates] = useState<Record<string, boolean>>({});
    const quickSearchFunctions = QuickSearchFunctions()
    const dispatch = useDispatch()
    const activeChartId = useSelector(selectActiveChartId);
    const recentItems = useSelector((state: RootState) =>
        activeChartId === ViewType.SYMBOL
            ? state.recentSearchStore?.recentSymbol ?? []
            : state.recentSearchStore?.recentChart ?? []
    );
    const chartTypes = useSelector(selectChartTypes)
    const statusLineSettings = useSelector(selectChartConfigSettings(viewKey))
    const quickSettings = useQuickSearchSettings();
    const [search, setSearch] = useState('')
    const showSplit = useMemo(() => activeChartId !== ViewType.SYMBOL, [activeChartId])
    const selectedChartType = useMemo(() => chartConfig?.chartType ?? ChartType.CANDLESTICK, [chartConfig])

    useEffect(() => {
        if (!open) {
            setSearch('');
        }
    }, [open]);

    useEffect(() => {
        const newStates = quickSettings.reduce((acc, setting) => {
            acc[setting.field] = setting.isChecked ?? false;
            return acc;
        }, {} as Record<string, boolean>);
        setCheckboxStates(newStates);
    }, [statusLineSettings])

    const onDrawingClick = useCallback(
        (drawingType: DrawingTypes, options: Record<string, any>, enter?: boolean) => {
            if (!chart) return
            if (enter) {
                chart.drawingManager.addDrawing(drawingType, DrawingDisplayType.DRAWING, options, {})
            } else {
                chart.drawingManager.abort()
            }
        },
        [chart]
    )

    const chartTypeSearchItems = useMemo(() => {
        return chartTypes.map((ct: any) => ({
            label: ct.name,
            type: 'chartType',
            code: ct.code,
            actionType: 'chartType',
        }));
    }, [chartTypes]);

    const quickSearchFunctionItems = useMemo(() => {
        return quickSearchFunctions.filter(
            (item: any) => item.actionType === 'functions'
        );
    }, [quickSearchFunctions]);

    const drawingSearchItems = useMemo(() => {
        return combinedDrawingToolItems.map((item: any) => ({
            label: item.text,
            type: "drawing",
            typeDrawing: item.drawingType,
            drawingToolId: item.drawingToolId,
            icon: item.icon,
            toolOptions: item.toolOptions,
        }))
    }, [])
    const handleCheckboxChange = (field: string) => {
        setCheckboxStates(prevState => ({
            ...prevState,
            [field]: !prevState[field]
        }))
    }

    const settingsSearchItems = useMemo(() => {
        return quickSettings.map((setting) => ({
            label: setting.label,
            type: 'setting',
            actionType: setting.actionType,
            settingId: setting.settingId,
            isChecked: checkboxStates[setting.field],
            field: setting.field,
        }));
    }, [checkboxStates])

    const allSearchItems = useMemo(() => {
        return [...chartTypeSearchItems, ...quickSearchFunctionItems, ...drawingSearchItems, ...settingsSearchItems]
    }, [chartTypeSearchItems, drawingSearchItems, settingsSearchItems])

    const filteredItems = useMemo(() => {
        if (!search) return recentItems
        return allSearchItems.filter((item: any) =>
            item.label.toLowerCase().includes(search.toLowerCase())
        )
    }, [search, allSearchItems, recentItems])

    const groupedItems = useMemo(() => {
        const groups: Record<string, { title: string; items: any[] }> = {}

        filteredItems?.forEach((item: any) => {
            const key = search ? item.actionType ?? item.type : 'recent'
            if (!groups[key]) {
                groups[key] = {
                    title:
                        search
                            ? key === 'chartType'
                                ? 'CHART TYPE'
                                : key === 'drawing'
                                    ? 'DRAWINGS'
                                    : key === "setting" ? 'SETTINGS' : key === 'functions' ? 'FUNCTIONS' : 'RECENTLY SEARCHED'
                            : 'RECENTLY SEARCHED',
                    items: [],
                }
            }

            groups[key].items.push(item)
        })

        return groups
    }, [filteredItems, search])
    const handleItemClick = (item: any) => {
        if (item.type === 'chartType') {
            const chartType = chartTypeFromJSON(item.code)
            if (selectedChartType !== chartType) {
                dispatch(updateChartType({viewKey, chartType}))
            }
        } else if (item.type === 'drawing') {
            onDrawingClick(item.typeDrawing, item.toolOptions ?? {}, true)
        } else if (item.type === 'fullscreen') {
            onToggleFullscreen(appContext)
        } else if (item.type === 'createAlert') {
            handleCreateAlert(appContext)
        } else if (item.clickHandlerId) {
            listOfFunctions[item.clickHandlerId](appContext)
        } else if (item.settingId) {
            settingsFunction[item.settingId](item, viewKey, statusLineSettings ?? {})
        }

        dispatch(addRecentItem({context: showSplit ? 'chart' : 'symbol', item: {...item, onClick: undefined, isChecked: undefined}}))
        onClose()
    }
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
            sx: {
                minHeight: "40%", maxHeight: '60%'
            }
        }}>
            <DialogTitle sx={{ pb: '5px' }}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>Search tool or function</Typography>
                    <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                        <ClearIcon fontSize='small'/>
                    </ExtraSmallButton>
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <SearchIcon sx={{fontSize: 24, mr: 1}}/>
                    <InputBase
                        size={"small"}
                        autoFocus
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>
            </DialogTitle>
            <Divider/>
            <DialogContent style={{padding: 0}}>
                {Object.entries(groupedItems).map(([type, group], i) => (
                    <Box key={type}>
                        {i > 0 && <Divider sx={{padding: 0}}/>}
                        <Typography
                            variant="subtitle2"
                            sx={{fontSize: '11px', color: 'text.secondary', mb: 1, padding: "20px 0px 0px 20px"}}

                        >
                            {group.title}
                        </Typography>
                        <Box sx={{p: '0px 0px 0px 20px'}}>
                            {group.items.map((item, index) => {
                                const knownTypes = [
                                    'removeDrawings',
                                    'fullscreen',
                                    'deletePane',
                                    'splitHorizontal',
                                    'splitVertical',
                                    'createAlert'
                                ]
                                const icon = item.type === 'setting'
                                    ? (
                                        <Checkbox
                                            size="small"
                                            checked={checkboxStates[item.field]}
                                            onChange={() => handleCheckboxChange(item.field)}
                                            sx={{p: 0}}
                                        />
                                    )
                                    : knownTypes.includes(item.type)
                                        ? iconMap[item.icon]
                                        : item.type === 'chartType'
                                            ? getIcon(chartTypeFromJSON(item.code))
                                            : getDrawingIcon(item.typeDrawing);
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 0px',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleItemClick(item)}
                                    >
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            {icon as any}
                                            <Typography variant="body1" fontSize={'14px'}>
                                                {item.label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};
export default QuickSearchDialog;
